import { useState, useEffect } from 'react';
import AdminSidebar from '../../components/AdminSidebar';
import { apiKeysList, apiKeysCreate, apiKeysPatch, apiKeysDelete } from '../../lib/adminApi';

const ORANGE = '#F37428';
const NAVY   = '#0a2540';

function timeAgo(d) {
  if (!d) return '—';
  const m = Math.floor((Date.now() - new Date(d)) / 60000);
  if (m < 60)  return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)  return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function Badge({ on }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700,
      background: on ? 'rgba(16,185,129,0.1)' : 'rgba(148,163,184,0.15)',
      color: on ? '#059669' : '#94a3b8',
      border: `1px solid ${on ? 'rgba(16,185,129,0.3)' : 'rgba(148,163,184,0.3)'}`,
    }}>{on ? 'Active' : 'Inactive'}</span>
  );
}

export default function ApiKeys() {
  const [keys, setKeys]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [creating, setCreating] = useState(false);
  const [showNew, setShowNew]   = useState(false);
  const [newForm, setNewForm]   = useState({ tenant_id: '', description: '' });
  const [revealed, setRevealed] = useState({});   // key_id → true when shown
  const [copied, setCopied]     = useState(null);

  async function load() {
    setLoading(true);
    try {
      const r = await apiKeysList();
      setKeys(r?.keys || []);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleCreate() {
    if (!newForm.tenant_id) return alert('Enter a tenant ID');
    setCreating(true);
    try {
      const r = await apiKeysCreate({
        tenant_id:   parseInt(newForm.tenant_id, 10),
        description: newForm.description.trim() || null,
      });
      if (r?.key_value) {
        setRevealed(prev => ({ ...prev, [r.key_id]: true }));
        setShowNew(false);
        setNewForm({ tenant_id: '', description: '' });
        await load();
      } else {
        alert(r?.error || 'Failed to create key');
      }
    } finally {
      setCreating(false);
    }
  }

  async function handleToggle(k) {
    await apiKeysPatch(k.key_id, { is_active: !k.is_active });
    setKeys(prev => prev.map(x => x.key_id === k.key_id ? { ...x, is_active: !x.is_active } : x));
  }

  async function handleDelete(k) {
    if (!confirm(`Delete key for ${k.company_name}? This cannot be undone.`)) return;
    await apiKeysDelete(k.key_id);
    setKeys(prev => prev.filter(x => x.key_id !== k.key_id));
  }

  function handleCopy(keyValue, keyId) {
    navigator.clipboard.writeText(keyValue).then(() => {
      setCopied(keyId);
      setTimeout(() => setCopied(null), 2000);
    });
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f0f5fb',
      fontFamily: "'Inter', system-ui, sans-serif" }}>
      <AdminSidebar active="api-keys" />
      <main style={{ flex: 1, overflowY: 'auto' }}>

        {/* Header */}
        <div style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #0f3460 100%)`,
          padding: '28px 36px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#fff' }}>API Keys</h1>
              <p style={{ margin: '4px 0 0', color: 'rgba(255,255,255,0.55)', fontSize: 13 }}>
                Partner API keys for the /api/enrich/single endpoint
              </p>
            </div>
            <button onClick={() => setShowNew(true)} style={{
              background: ORANGE, color: '#fff', border: 'none', borderRadius: 8,
              padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}>+ New Key</button>
          </div>
        </div>

        <div style={{ padding: '28px 36px' }}>

          {/* New key form */}
          {showNew && (
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
              padding: '20px 24px', marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: NAVY, marginBottom: 14 }}>Create New API Key</div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div>
                  <label style={s.label}>Tenant ID</label>
                  <input type="number" value={newForm.tenant_id} placeholder="e.g. 39"
                    onChange={e => setNewForm(f => ({ ...f, tenant_id: e.target.value }))}
                    style={{ ...s.input, width: 100 }} />
                </div>
                <div>
                  <label style={s.label}>Description</label>
                  <input type="text" value={newForm.description} placeholder="e.g. Ultimaster — n8n integration"
                    onChange={e => setNewForm(f => ({ ...f, description: e.target.value }))}
                    style={{ ...s.input, width: 300 }} />
                </div>
                <button onClick={handleCreate} disabled={creating} style={s.btnPrimary}>
                  {creating ? 'Creating…' : 'Create'}
                </button>
                <button onClick={() => setShowNew(false)} style={s.btnSecondary}>Cancel</button>
              </div>
            </div>
          )}

          {/* Keys table */}
          {loading ? (
            <div style={{ color: '#94a3b8', fontSize: 13, padding: '40px 0', textAlign: 'center' }}>
              Loading…
            </div>
          ) : keys.length === 0 ? (
            <div style={{ color: '#94a3b8', fontSize: 13, padding: '60px 0', textAlign: 'center' }}>
              No API keys yet — create one above
            </div>
          ) : (
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {['Tenant', 'Description', 'Key', 'Status', 'Created', 'Last used', 'Calls', ''].map(h => (
                      <th key={h} style={s.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {keys.map(k => (
                    <tr key={k.key_id} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td style={s.td}>
                        <div style={{ fontWeight: 600, color: NAVY, fontSize: 13 }}>{k.company_name}</div>
                        <div style={{ fontSize: 10, color: '#94a3b8' }}>tenant #{k.tenant_id}</div>
                      </td>
                      <td style={{ ...s.td, fontSize: 12, color: '#374151', maxWidth: 200 }}>
                        {k.description || <span style={{ color: '#94a3b8' }}>—</span>}
                      </td>
                      <td style={s.td}>
                        {revealed[k.key_id] ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <code style={{ fontSize: 10, background: '#f8fafc', border: '1px solid #e2e8f0',
                              borderRadius: 5, padding: '3px 7px', color: '#374151', letterSpacing: '0.02em' }}>
                              {k.key_value}
                            </code>
                            <button onClick={() => handleCopy(k.key_value, k.key_id)} style={s.btnTiny}>
                              {copied === k.key_id ? '✓' : 'Copy'}
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <code style={{ fontSize: 11, color: '#94a3b8' }}>mfai_live_••••••••</code>
                            <button onClick={() => setRevealed(p => ({ ...p, [k.key_id]: true }))}
                              style={s.btnTiny}>Show</button>
                          </div>
                        )}
                      </td>
                      <td style={s.td}>
                        <Badge on={k.is_active} />
                      </td>
                      <td style={{ ...s.td, fontSize: 11, color: '#64748b' }}>
                        {new Date(k.created_at).toLocaleDateString()}
                      </td>
                      <td style={{ ...s.td, fontSize: 11, color: '#64748b' }}>
                        {timeAgo(k.last_used_at)}
                      </td>
                      <td style={s.td}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: NAVY }}>
                          {k.total_calls || 0}
                        </div>
                        {k.total_calls > 0 && (
                          <div style={{ fontSize: 10, color: '#94a3b8' }}>
                            {k.successful_calls}/{k.total_calls} ok
                          </div>
                        )}
                      </td>
                      <td style={{ ...s.td, textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          <button onClick={() => handleToggle(k)}
                            style={{ ...s.btnTiny, color: k.is_active ? '#64748b' : '#059669' }}>
                            {k.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button onClick={() => handleDelete(k)}
                            style={{ ...s.btnTiny, color: '#dc2626' }}>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Usage note */}
          <div style={{ marginTop: 20, padding: '12px 16px', background: '#fff',
            border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 12, color: '#64748b' }}>
            <strong style={{ color: NAVY }}>Endpoint:</strong> POST https://masterfileai-api.azurewebsites.net/api/enrich/single?token=&lt;key&gt;
            &nbsp;&nbsp;·&nbsp;&nbsp;
            <strong style={{ color: NAVY }}>Billing:</strong> 50% of standard rate per vendor enriched
          </div>

          {/* ── API Reference ─────────────────────────────────────────────── */}
          <div style={{ marginTop: 36 }}>
            <h2 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: NAVY }}>
              API Reference
            </h2>

            {/* Authentication */}
            <ApiSection title="Authentication">
              <p style={s.docP}>
                Pass your API key as a query parameter on every request. Keys are prefixed <code style={s.code}>mfai_live_</code> and are tenant-scoped — each key only accesses data for the tenant it was created for.
              </p>
              <CodeBlock>{`POST https://masterfileai-api.azurewebsites.net/api/enrich/vendor?token=mfai_live_xxxxxxxxxx

Content-Type: text/plain   ← required (prevents CORS preflight)`}</CodeBlock>
            </ApiSection>

            {/* Request shape */}
            <ApiSection title="Request Body — POST /api/enrich/vendor">
              <p style={s.docP}>
                Submit one vendor per call. A vendor may have multiple address sites (remittance, shipping, etc.).
                Full enrichment (domain, NAICS, company profile) runs on the <strong>first address</strong>; all others get address standardization only.
              </p>
              <CodeBlock>{`{
  "request_id": "apg_SAP_V001_20260428",   // optional — echoed back in response
  "vendor": {
    "system_name":            "SAP",         // your ERP system label
    "customer_vendor_number": "V001",        // required — unique vendor # in your system
    "name_raw":               "Acme Corp",   // required — vendor name as-is from ERP
    "taxid_raw":              "12-3456789",  // optional

    "addresses": [                           // required — at least one entry
      {
        "site":            "HQ",             // site / address code from ERP
        "vrow_id":         12345,            // your internal row ID — echoed back unchanged
        "name_raw":        "Acme Corp",
        "address1_raw":    "123 Main St",
        "address2_raw":    null,
        "address3_raw":    null,
        "address4_raw":    null,
        "po_box_raw":      null,
        "city_raw":        "Boston",
        "state_raw":       "MA",
        "postal_code_raw": "02101",
        "country_raw":     "US",
        "telephone_raw":   "617-555-1234",
        "email_raw":       "ap@acme.com",
        "domain_raw":      null              // supply if already known — skips discovery
      }
      // additional address sites can follow...
    ]
  }
}`}</CodeBlock>
            </ApiSection>

            {/* Response shape */}
            <ApiSection title="Response Shape">
              <CodeBlock>{`{
  "ok": true,
  "request_id": "apg_SAP_V001_20260428",
  "enriched_at": "2026-04-28T14:32:00.000Z",

  "standardized": {
    "name":        "Acme Corporation",       // AI-cleaned legal name
    "name2":       null,
    "tax_id":      "12-3456789",
    "is_person":   false,                    // true if vendor is an individual, not a company
    "is_internal": false,                    // true if vendor looks like an internal entity

    "addresses": [
      {
        "site":               "HQ",          // echoed from input
        "vrow_id":            12345,         // echoed from input
        "address1_ai":        "123 Main Street",
        "address2_ai":        null,
        "address3_ai":        null,
        "address4_ai":        null,
        "po_box_ai":          null,
        "city_ai":            "Boston",
        "state_ai":           "MA",
        "postal_code_ai":     "02101-0000",
        "country_ai":         "US",
        "phone_formatted_ai": "(617) 555-1234",
        "phone_e164_ai":      "+16175551234",
        "email_ai":           "ap@acme.com"
      }
      // one entry per address site submitted
    ]
  },

  "company": {
    "name_recommended":  "Acme Corporation",
    "domain":            "acme.com",
    "domain_confidence": 95,                 // 0–100
    "what_they_do":      "Acme Corp manufactures industrial widgets...",
    "naics_code":        "332999",
    "naics_title":       "All Other Miscellaneous Fabricated Metal Product Manufacturing",
    "naics_confidence":  88,                 // 0–100
    "key_offerings":     ["Industrial widgets", "Custom fabrication"],
    "industries_served": ["Manufacturing", "Construction"]
  },

  "processing": {
    "path": "known"                          // "pre_supplied_verified" | "known" | "searched"
  },

  "error": null                              // non-null string on partial failure
}`}</CodeBlock>
              <div style={{ marginTop: 12 }}>
                <FieldTable rows={[
                  ['processing.path', '"pre_supplied_verified"', 'domain_raw was supplied and verified by Jina — no AI lookup needed'],
                  ['processing.path', '"known"', 'Found via Claude training data (Step 1 or 2) — no web search'],
                  ['processing.path', '"searched"', 'Required web search (Step 3 or 4) to resolve'],
                  ['domain_confidence', '0–100', 'AI confidence in the domain match. ≥85 = high confidence'],
                  ['is_person', 'bool', 'Vendor appears to be an individual (sole trader, consultant, etc.)'],
                  ['is_internal', 'bool', 'Vendor looks like an internal entity (petty cash, payroll, intercompany)'],
                ]} />
              </div>
            </ApiSection>

            {/* Export shape */}
            <ApiSection title="Master File Export — GET /api/master-file/export">
              <p style={s.docP}>
                Returns all enriched vendor records for the authenticated tenant. One row per address site per vendor.
                Authenticate with a user JWT token (not an API key) — this endpoint is for client portal use.
              </p>
              <CodeBlock>{`GET https://masterfileai-api.azurewebsites.net/api/master-file/export?token=<jwt>

Response: { "rows": [ ...one object per address row... ] }`}</CodeBlock>
              <div style={{ marginTop: 12 }}>
                <FieldTable rows={[
                  ['master_record_id', 'int', 'Unique ID for the vendor record'],
                  ['system_name', 'string', 'ERP system label (e.g. "SAP", "Oracle")'],
                  ['vendor_num', 'string', 'Vendor # from source ERP'],
                  ['vendor_name_source', 'string', 'Original vendor name from upload'],
                  ['company_name_recommended', 'string', 'AI-cleaned legal company name'],
                  ['domain_value', 'string', 'Company website domain (e.g. "acme.com")'],
                  ['domain_confidence', 'int', 'Confidence score 0–100'],
                  ['domain_source', 'string', 'How domain was found: supplied / training / searched'],
                  ['naics_code', 'string', '6-digit NAICS industry code'],
                  ['naics_title', 'string', 'NAICS industry description'],
                  ['naics_confidence', 'int', 'NAICS confidence score 0–100'],
                  ['what_they_do', 'string', 'AI-generated company profile paragraph'],
                  ['key_offerings', 'string (JSON array)', 'Main products/services'],
                  ['industries_served', 'string (JSON array)', 'Industries the vendor serves'],
                  ['is_person', 'bit', '1 = individual vendor, not a company'],
                  ['is_internal', 'bit', '1 = internal / intercompany entity'],
                  ['last_enriched_at', 'datetime', 'When this vendor was last enriched'],
                  ['total_addresses', 'int', 'Number of address sites on file'],
                  ['dup_domain_same', 'string', 'Group ID linking vendors with same domain within same system'],
                  ['dup_domain_cross', 'string', 'Group ID linking vendors with same domain across systems'],
                  ['dup_address', 'string', 'Group ID linking vendors with same standardized address'],
                  ['address_seq', 'int', 'Address sequence number (1 = primary)'],
                  ['address_line_1–4', 'string', 'AI-standardized address lines'],
                  ['city / state_province', 'string', 'Standardized city and state'],
                  ['zip_postal_code', 'string', 'Standardized postal code'],
                  ['country', 'string', 'ISO country code (e.g. "US")'],
                  ['phone_formatted', 'string', 'Human-readable phone (e.g. "(617) 555-1234")'],
                  ['phone_e164', 'string', 'E.164 format phone (e.g. "+16175551234")'],
                  ['email_std', 'string', 'Standardized/verified email address'],
                  ['po_box_std', 'string', 'Standardized PO Box if present'],
                ]} />
              </div>
            </ApiSection>

            {/* Error codes */}
            <ApiSection title="Error Codes">
              <FieldTable rows={[
                ['400', 'Bad Request', 'Missing required fields (vendor, customer_vendor_number, name_raw, addresses)'],
                ['401', 'Unauthorized', 'Missing token, invalid key, or key is deactivated'],
                ['503', 'Service Unavailable', 'Anthropic API quota exceeded or key invalid — enrichment halted'],
                ['500', 'Internal Error', 'Unexpected enrichment failure — check request_id for support'],
              ]} />
            </ApiSection>

          </div>
          {/* ── /API Reference ────────────────────────────────────────────── */}

        </div>
      </main>
    </div>
  );
}

function ApiSection({ title, children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
      marginBottom: 16, overflow: 'hidden' }}>
      <div style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0',
        padding: '10px 20px' }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#0a2540' }}>{title}</span>
      </div>
      <div style={{ padding: '16px 20px' }}>{children}</div>
    </div>
  );
}

function CodeBlock({ children }) {
  return (
    <pre style={{
      margin: 0, padding: '14px 16px', background: '#0f172a', color: '#e2e8f0',
      borderRadius: 8, fontSize: 11.5, lineHeight: 1.65, overflowX: 'auto',
      fontFamily: "'Fira Code', 'Cascadia Code', 'Consolas', monospace',",
      whiteSpace: 'pre',
    }}>{children}</pre>
  );
}

function FieldTable({ rows }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5 }}>
      <thead>
        <tr style={{ background: '#f8fafc' }}>
          {['Field / Code', 'Type / Value', 'Description'].map(h => (
            <th key={h} style={{ padding: '7px 12px', textAlign: 'left', fontWeight: 700,
              color: '#94a3b8', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em',
              borderBottom: '1px solid #e2e8f0' }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map(([f, t, d], i) => (
          <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
            <td style={{ padding: '7px 12px' }}><code style={{ fontSize: 11, color: '#F37428' }}>{f}</code></td>
            <td style={{ padding: '7px 12px', color: '#475569', whiteSpace: 'nowrap' }}>{t}</td>
            <td style={{ padding: '7px 12px', color: '#64748b' }}>{d}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const s = {
  label:       { display: 'block', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 },
  input:       { padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 12, background: '#fff', color: '#1e293b' },
  btnPrimary:  { background: ORANGE, color: '#fff', border: 'none', borderRadius: 7, padding: '7px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  btnSecondary:{ background: '#fff', color: '#374151', border: '1px solid #e2e8f0', borderRadius: 7, padding: '7px 14px', fontSize: 12, cursor: 'pointer' },
  btnTiny:     { background: 'none', border: '1px solid #e2e8f0', borderRadius: 5, padding: '3px 8px', fontSize: 11, color: '#374151', cursor: 'pointer' },
  th:          { fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '9px 14px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' },
  td:          { padding: '12px 14px', verticalAlign: 'middle' },
  docP:        { margin: '0 0 12px', fontSize: 12.5, color: '#475569', lineHeight: 1.6 },
  code:        { fontSize: 11, background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 4, padding: '1px 6px', color: '#0f172a', fontFamily: 'monospace' },
};
